import { useCallback, useMemo } from 'react';
import type { PermissionKey } from '@evimo/types/enums/permission-key';
import { useAuth } from '~/app/auth/context/AuthContext';

export function usePermission() {
  const { user } = useAuth();

  const permissions = useMemo(
    () => user?.permissions ?? [],
    [user?.permissions],
  );

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const hasPermission = useCallback(
    (key: PermissionKey) => permissionSet.has(key),
    [permissionSet],
  );

  const hasAllPermissions = useCallback(
    (keys: readonly PermissionKey[]) =>
      keys.every((key) => permissionSet.has(key)),
    [permissionSet],
  );

  const hasAnyPermission = useCallback(
    (keys: readonly PermissionKey[]) =>
      keys.some((key) => permissionSet.has(key)),
    [permissionSet],
  );

  return {
    permissions,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
  };
}
